import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "./Common/AdminLayout";
import { useDispatch, useSelector } from "react-redux";
import { JobApproval, getAllJobs } from "../../Redux/Slices/JobPost";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatDate } from "../../Helper/FromatTime";
import LoadingSkeleton from "./Common/LoadingSkeleton";
import "../../asset/new/css/admin-modern.css";

const ApproveJobs = () => {
  const { allJobs } = useSelector((state) => state?.Jobs);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL parameters
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page")) || 1);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [loading, setLoading] = useState(true);

  // Update URL parameters when state changes
  const updateURLParams = useCallback((page, search = "") => {
    const params = new URLSearchParams();
    if (page) params.set("page", page.toString());
    if (search) params.set("search", search);
    
    setSearchParams(params);
  }, [setSearchParams]);

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearchQuery(searchValue);
    setCurrentPage(1); // Reset to page 1 when searching
    
    let data = {
      admin_approval: "pending",
      page: 1,
      search: searchValue,
    };
    
    dispatch(getAllJobs(data));
    updateURLParams(1, searchValue);
  };

  const statusHandler = async (id, data) => {
    const userClickedOK = window.confirm("Are you sure you want to proceed?");
    if (userClickedOK) {
      const action = { action: data };
      // API call
      await JobApproval(id, action);
      
      // Refresh the job list while maintaining current page and search
      const userData = {
        admin_approval: "pending",
        page: currentPage,
      };
      
      if (searchQuery) {
        userData.search = searchQuery;
      }
      
      dispatch(getAllJobs(userData));
    }
  };

  const handlePagination = (page) => {
    setCurrentPage(page);
    updateURLParams(page, searchQuery);
    
    const data = {
      admin_approval: "pending",
      page,
    };
    
    if (searchQuery) {
      data.search = searchQuery;
    }
    
    dispatch(getAllJobs(data));
  };

  // Navigate to job detail while preserving current state
  const navigateToJobDetail = (jobId) => {
    // Store current state in URL before navigating
    updateURLParams(currentPage, searchQuery);
    navigate(`/admin/reviewJob/${jobId}`);
  };

  // Fetch data based on current state
  const fetchData = useCallback(async () => {
    setLoading(true);
    const data = {
      admin_approval: "pending",
      page: currentPage,
    };
    
    if (searchQuery) {
      data.search = searchQuery;
    }
    
    await dispatch(getAllJobs(data));
    setLoading(false);
  }, [currentPage, searchQuery, dispatch]);

  useEffect(() => {
    fetchData();
    document.title = "Approve Jobs | Admin";
  }, [fetchData]);

  // Calculate visible page range for better pagination UI
  let startPage = 1;
  let endPage = 1;
  const maxVisiblePages = 5;

  if (allJobs?.pagination) {
    const totalPages = allJobs.pagination.totalPages;
    const current = currentPage;
    
    startPage = Math.max(1, current - Math.floor(maxVisiblePages / 2));
    endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
  }

  return (
    <AdminLayout
      title="Approve Jobs"
      subtitle="Review and approve pending job postings"
      breadcrumbs={[
        { label: "Admin", active: false },
        { label: "Approve Jobs", active: true }
      ]}
    >
      {/* Search Bar */}
      <div className="admin-card" style={{ marginBottom: 'var(--spacing-xl)' }}>
        <div className="admin-card-body">
          <input
            type="search"
            id="form1"
            placeholder="Search by job title, company, or industry..."
            autoComplete="off"
            className="admin-input"
            value={searchQuery}
            onChange={handleSearch}
            style={{ maxWidth: '500px' }}
          />
        </div>
      </div>

     {/* Table Card */}
      <div className="admin-card">
        <div className="admin-card-body" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table className="admin-table" style={{ minWidth: '800px', width: '100%' }}>
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Job Title</th>
                  <th>Posted By</th>
                  <th>Posted Date</th>
                  <th>Industry</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <LoadingSkeleton type="table-row" count={5} columns={6} />
                ) : allJobs?.docs && allJobs.docs.length > 0 ? (
                  allJobs.docs.map((value, index) => (
                    <tr key={value._id || index}>
                      <td>
                        <span className="admin-badge admin-badge-light">
                          {(currentPage - 1) * (allJobs.pagination?.limit || 10) + index + 1}
                        </span>
                      </td>
                      <td>
                        <div className="admin-text-truncate" style={{ maxWidth: "250px" }}>
                          {value?.title}
                        </div>
                      </td>
                      <td>{value?.created_by?.name || "N/A"}</td>
                      <td>{formatDate(value?.posted_date)}</td>
                      <td>{value?.industry?.name || "N/A"}</td>
                      <td>
                        <div className="admin-btn-group">
                          <button
                            onClick={() => navigateToJobDetail(value?._id)}
                            className="admin-btn admin-btn-sm admin-btn-info"
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i> View
                          </button>
                          <button
                            onClick={() => statusHandler(value?._id, "approved")}
                            className="admin-btn admin-btn-sm admin-btn-success"
                            title="Approve"
                          >
                            <i className="fas fa-check"></i> Approve
                          </button>
                          <button
                            onClick={() => statusHandler(value?._id, "rejected")}
                            className="admin-btn admin-btn-sm admin-btn-danger"
                            title="Reject"
                          >
                            <i className="fas fa-times"></i> Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6">
                      <div className="admin-empty-state">
                        <i className="fas fa-clipboard-check fa-3x mb-3"></i>
                        <h4>No Pending Jobs</h4>
                        <p>
                          {searchQuery
                            ? "No jobs found matching your search"
                            : "There are no pending job requests at the moment."}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {allJobs?.pagination && allJobs.pagination.totalPages > 1 && (
            <div className="admin-pagination">
              <button
                className="admin-pagination-btn"
                onClick={() => allJobs.pagination.prevPage && handlePagination(allJobs.pagination.prevPage)}
                disabled={!allJobs.pagination.prevPage}
              >
                <i className="fas fa-chevron-left"></i>
                Previous
              </button>

              <div className="admin-pagination-numbers">
                {startPage > 1 && (
                  <>
                    <button
                      className="admin-pagination-number"
                      onClick={() => handlePagination(1)}
                    >
                      1
                    </button>
                    {startPage > 2 && <span className="admin-pagination-dots">...</span>}
                  </>
                )}

                {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((page) => (
                  <button
                    key={page}
                    className={`admin-pagination-number ${currentPage === page ? "active" : ""}`}
                    onClick={() => handlePagination(page)}
                  >
                    {page}
                  </button>
                ))}

                {endPage < allJobs.pagination.totalPages && (
                  <>
                    {endPage < allJobs.pagination.totalPages - 1 && (
                      <span className="admin-pagination-dots">...</span>
                    )}
                    <button
                      className="admin-pagination-number"
                      onClick={() => handlePagination(allJobs.pagination.totalPages)}
                    >
                      {allJobs.pagination.totalPages}
                    </button>
                  </>
                )}
              </div>

              <button
                className="admin-pagination-btn"
                onClick={() => allJobs.pagination.nextPage && handlePagination(allJobs.pagination.nextPage)}
                disabled={!allJobs.pagination.nextPage}
              >
                Next
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}

          {/* Pagination Info */}
          {allJobs?.pagination && (
            <div className="admin-table-info">
              Showing {((currentPage - 1) * (allJobs.pagination.limit || 10)) + 1} to{" "}
              {Math.min(currentPage * (allJobs.pagination.limit || 10), allJobs.pagination.totalDocs)} of{" "}
              {allJobs.pagination.totalDocs} entries
              {searchQuery && " (filtered)"}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default ApproveJobs;